import { React, useState, useEffect, useCallback } from "react";
import { AiOutlineHome, AiOutlineFire } from "react-icons/ai";
import { BsCurrencyExchange, BsCurrencyBitcoin } from "react-icons/bs";
import { TiChartArea } from "react-icons/ti";
import { RiAccountCircleLine, RiBubbleChartLine } from "react-icons/ri";
import { isMobile } from "react-device-detect";
import { SearchBarMemo } from "../SearchBar";
import { useHistory, useLocation } from "react-router-dom";
import { projectPrimaryColor } from "../../generalConfigs";
import HeaderNotifications from "../HeaderNotifications";
import { HeaderProfileMemo } from "../HeaderProfile";
import { FiSettings } from "react-icons/fi";
import { useUser } from "../../hooks/useUser";
import ThemeToggler from "../ThemeToggler/ThemeToggler";
import GradientText from "../Logo";
import { useTour } from "@reactour/tour";
import useGeneralContext from "../../context/GeneralContext";
import { MdOutlineWorkspacePremium } from "react-icons/md";
import useSubscription from "../../hooks/useSubscription";

const marginTopHeader = !isMobile && -10;

export default function Header() {
  const [yPosition, setYPosition] = useState(window.scrollY);
  const [toggleHeaderMobile, setToggleHeaderMobile] = useState(0);
  const [headerPosition, setHeaderPosition] = useState("sticky");
  const [userData, setUserData] = useState(null);
  const [actualNav, setActualNav] = useState({ page: null, subPage: null });
  const { getUserDetails, isAuthenticated } = useUser();
  const [isPremium, setIsPremium] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const actualURL = location.pathname;
  const { currentStep } = useTour();
  const { tourStart } = useGeneralContext();
  const pathname = useLocation().pathname;
  const { isUserPremiumUncached } = useSubscription();


  //Fetch user Details
  useEffect(() => {
    //Check if userDetails are in cache.
    let userCachedData = localStorage.getItem("userDetails");
    if (userCachedData) {
      setUserData(JSON.parse(userCachedData));
    } else {
      //If not in cache, let's retrieve from backend.

      let fetchData = async () => {
        const isAuth = isAuthenticated();
        if (isAuth) {
          isUserPremiumUncached().then((res) => setIsPremium(res));

          let request = await getUserDetails();
          if (request) {
            setUserData(request.data);
          }
        }
      };
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const navbarItems = isPremium ? navbarItemsPremium : navbarItemsFree;

  //Make header sticky corretly on mobile
  const handleNavigation = useCallback(
    (e) => {
      const window = e.currentTarget;
      if (yPosition > window.scrollY) {
        setToggleHeaderMobile(0);
        setHeaderPosition("sticky");
      }
      setYPosition(window.scrollY);
    },
    [yPosition]
  );

  //Get the scroll event and set Y position.
  useEffect(() => {
    setYPosition(window.scrollY);
    window.addEventListener("scroll", handleNavigation);

    return () => {
      window.removeEventListener("scroll", handleNavigation);
    };
  }, [handleNavigation]);

  //Hide header when clicks on hamburguer Menu (Mobile)
  const hideHeaderHandle = () => {
    setToggleHeaderMobile(false);
    setHeaderPosition(false);
  };

  //Mark the correct navbar button on page load.
  useEffect(() => {
    navbarItems.forEach((item, i) => {
      if (item.link === actualURL) {
        setActualNav({
          page: i,
          subItem: null,
        });
      }
    });
  }, [actualURL]);

  //Determines if an button on navbar is selected ...
  //if not, remove the state from navbar buttons.
  useEffect(() => {
    let markButton = false;
    navbarItems.forEach((item) => {
      if (item.link === actualURL) {
        markButton = true;
      }
      //Determine if an subTitle is selected.
      if (item.subTitles) {
        item.subTitles.forEach((subItem) => {
          if (subItem.link === actualURL) {
            markButton = true;
          }
        });
      }
    });
    if (!markButton) {
      setActualNav({ page: null, subPage: null });
    }
  }, [actualURL]);

  return (
    <div
      className="az-header"
      style={
        userData
          ? {
              marginTop: marginTopHeader,
              position: headerPosition,
              top: toggleHeaderMobile,
              zIndex: 100,
            }
          : { display: "none" }
      }
    >
      <div className="container">
        {
          //Project Logos & Hamburguer Menu on Mobile.
          <div className="az-header-left">
            {
              //Logo
              <span
                onClick={() => {
                  history.push({
                    pathname: "/dashboard",
                    state: { page: 0, subPage: null },
                  });
                  return setActualNav({ page: 0, subPage: null });
                }}
                className="az-logo cursor-pointer"
                style={{ color: "black" }}
              >
                <GradientText text={"FatoresAI"} />
              </span>
            }
            {
              //Hamburguer Menu
              <a
                href="#/"
                id="azNavShow"
                className="az-header-menu-icon d-lg-none"
                onClick={() => hideHeaderHandle()}
              >
                <span />
              </a>
            }
          </div>
        }

        {
          //NavBar items
          <div className="az-navbar az-navbar-three">
            <div>
              <span
                onClick={() => {
                  history.push({
                    pathname: "/dashboard",
                    state: { page: 0, subPage: null },
                  });
                  return setActualNav({ page: 0, subPage: null });
                }}
                className="az-logo hideOnClick cursor-pointer"
                style={{ color: "black" }}
              >
                <GradientText text={"FatoresAI"} />
              </span>
            </div>
            <ul className="nav">
              {navbarItems.map((item, i) => {
                return (
                  <li className="nav-item" key={i}>
                    <span
                      id={
                        !isMobile
                          ? `stepeable-${item.title.replace(" ", "")}`
                          : ""
                      }
                      onClick={() => {
                        if (item.link) {
                          history.push({
                            pathname: item.link,
                            state: { page: i, subPage: null },
                          });
                          return setActualNav({ page: i, subPage: null });
                        }
                      }}
                      className={
                        item.subTitles
                          ? "nav-link with-sub cursor-pointer"
                          : "nav-link hideOnClick cursor-pointer"
                      }
                    >
                      <i
                        //Highlight icon of the selected nav
                        style={
                          actualNav.page === i
                            ? { color: projectPrimaryColor }
                            : null
                        }
                      >
                        {item.icon}
                      </i>
                      {item.title}
                    </span>

                    <nav className="nav-sub">
                      {
                        //Subtitles inside nav.
                        item.subTitles &&
                          item.subTitles.map((subItem, subKey) => {
                            return (
                              <span
                                onClick={() => {
                                  setActualNav({
                                    page: i,
                                    subPage: subKey,
                                  });
                                  return history.push(subItem.link);
                                }}
                                className="nav-sub-link hideOnClick cursor-pointer"
                                key={subKey}
                                style={{
                                  marginTop: "5px",
                                }}
                              >
                                <i
                                  style={
                                    subKey === actualNav.subPage
                                      ? {
                                          color: projectPrimaryColor,
                                          marginRight: 4,
                                        }
                                      : { marginRight: 4 }
                                  }
                                >
                                  {subItem.icon}
                                </i>
                                {subItem.title}
                              </span>
                            );
                          })
                      }
                    </nav>
                  </li>
                );
              })}
            </ul>
          </div>
        }

        {
          //Navbar buttons (Search, notifications, profile)
          <div className="az-header-right">
            <SearchBarMemo />

            <HeaderNotifications />

            <ThemeToggler />

            <HeaderProfileMemo userData={userData} />
          </div>
        }
      </div>
    </div>
  );
}

//!Make sure that the first object in this array is ever the 'dashboard'
const navbarItemsPremium = [
  {
    title: "Feed",
    link: "/dashboard",
    icon: <AiOutlineHome />,
    subTitles: null,
  },
  {
    title: "Melhores",
    link: null,
    icon: <AiOutlineFire />,
    subTitles: [
      {
        title: "Ações",
        link: "/ranking/stock",
        icon: <TiChartArea />,
      },
      {
        title: "Criptomoedas",
        link: "/ranking/crypto",
        icon: <BsCurrencyBitcoin />,
      },
    ],
  },
  {
    title: "Minha carteira B3",
    link: "/wallet-b3",
    icon: <RiBubbleChartLine />,
    subtitles: null,
  },
  {
    title: "Configurações",
    link: "/account-settings",
    icon: <FiSettings />,
    subTitles: null,
  },
];

//!Make sure that the first object in this array is ever the 'dashboard'
const navbarItemsFree = [
  {
    title: "Feed",
    link: "/dashboard",
    icon: <AiOutlineHome />,
    subTitles: null,
  },
  {
    title: "Melhores",
    link: null,
    icon: <AiOutlineFire />,
    subTitles: [
      {
        title: "Ações",
        link: "/ranking/stock",
        icon: <TiChartArea />,
      },
      {
        title: "Criptomoedas",
        link: "/ranking/crypto",
        icon: <BsCurrencyBitcoin />,
      },
    ],
  },
  {
    title: "Minha carteira B3",
    link: "/wallet-b3",
    icon: <RiBubbleChartLine />,
    subtitles: null,
  },

  {
    title: "Seja Premium (55% OFF)",
    link: "/billing",
    icon: <MdOutlineWorkspacePremium color="yellow" />,
    subtitles: null,
  },
];
